// suppress linter errors about d3 being undefined
// we require("../js/libs/d3.min.js") in app/static/frontend/index.jsx
/* global d3 */

import { useEffect } from "react"

// renderers
import {
    hideTooltip,
    renderTooltip,
    showTooltip,
} from "app/static/frontend/widgets/components/visualization/renderers/sharedRenderers"

// selectors
import { selectShowInSearchPermanentFilters } from "app/static/frontend/widgets/selectors/listWidgetSelectors"

// helpers
import { isIE } from "app/static/frontend/imports/desktopHelperFunctions"

export const renderStackedBars = ({
    colors,
    dataSources,
    editing,
    editForm,
    height,
    isExternal,
    isHorizontal,
    percentage,
    reference,
    // stacked
    series,
    showInSearch,
    timestamp,
    width,
    x,
    y,
}) =>
    useEffect(() => {
        if (timestamp && height && width) {
            d3
                .select(reference.current)
                .select(".bars")
                .remove()

            const tooltip = renderTooltip({ reference })

            d3
                .select(reference.current)
                // insert the BarStacked group before the Tooltip element due to svg rendering
                .insert("g", () => tooltip.node())

                .attr("class", "bars")
                .selectAll("g.stack")
                .data(series)
                // TODO: uncomment below and delete .enter().append("g") after d3 v5 upgrade
                // .join("g")
                // https://stackoverflow.com/a/55708965/6201291
                .enter()
                .append("g")

                .attr("class", "stack")
                .attr("data-cy", (d, i) => `bar-stacked-stack-${i}`)
                // this fills each bar in the bar graph with its corresponding color value
                // from the ColorPickerField(s) redux-form in the sidebar
                .style("fill", (d, i) => colors[i])

                .selectAll("rect")
                .data(d => d)
                // TODO: uncomment below and delete .enter().append("rect") after d3 v5 upgrade
                // .join("rect")
                // https://stackoverflow.com/a/55708965/6201291
                .enter()
                .append("rect")

                .attr("data-cy", (d, i) => `bar-stacked-rect-${d.keyIndex}-${i}`)
                .attr("x", (d) => {
                    if (isHorizontal) {
                        // x(d[0])
                        return x(d.y0)
                    }

                    // x(d.data.name))
                    return x(d.x)
                })
                .attr("y", (d) => {
                    if (isHorizontal) {
                        // y(d.data.name)
                        return y(d.x)
                    }

                    // y(d[1])
                    return y(d.y0 + d.y)
                })
                .attr("height", (d) => {
                    if (isHorizontal) {
                        // y.bandwidth()
                        return y.rangeBand()
                    }

                    // y(d[0]) - y(d[1])
                    return y(d.y0) - y(d.y0 + d.y)
                })
                // TODO: uncomment below and delete .attr("width", x.rangeBand()) after d3 v5 upgrade
                // https://github.com/d3/d3/blob/master/CHANGES.md#scales-d3-scale
                // https://github.com/d3/d3-scale/blob/master/README.md#band_bandwidth
                // .attr("width", x.bandwidth())

                // https://d3-wiki.readthedocs.io/zh_CN/master/Ordinal-Scales/#ordinal_rangeBand
                .attr("width", (d) => {
                    if (isHorizontal) {
                        // x(d[1]) - x(d[0])
                        return x(d.y0 + d.y) - x(d.y0)
                    }

                    // x.bandwidth()
                    return x.rangeBand()
                })
                .style("cursor", d => (
                    (
                        editing ||
                        editForm ||
                        isExternal ||
                        (
                            !dataSources[d.keyIndex].count &&
                            dataSources[d.keyIndex].group_by &&
                            !DjangIO.app.widgets.types.FrequencyTupleFields.by_value(
                                dataSources[d.keyIndex].group_by.frequency_tuple_field
                            ).filter_keys[dataSources[d.keyIndex].safed_search.data_type]
                        )
                    )
                        ? "unset"
                        : "pointer"
                ))
                .on("click", (d) => {
                    if (!editing && !editForm && !isExternal) {
                        const filterKey = (
                            !dataSources[d.keyIndex].count &&
                            dataSources[d.keyIndex].group_by &&
                            DjangIO.app.widgets.types.FrequencyTupleFields.by_value(
                                dataSources[d.keyIndex].group_by.frequency_tuple_field
                            ).filter_keys[dataSources[d.keyIndex].safed_search.data_type]
                        )

                        const additional_filters = dataSources[d.keyIndex].additional_filters ? dataSources[d.keyIndex].additional_filters : {}

                        showInSearch(
                            selectShowInSearchPermanentFilters({
                                ...(
                                    (filterKey || additional_filters)
                                        ? {
                                            filters: [{
                                                ...additional_filters,
                                                [filterKey]: d.xOriginal
                                            }]
                                        }
                                        : {}
                                ),
                                safedSearch: dataSources[d.keyIndex].safed_search,
                            })
                        )
                    }
                })
                // these event handlers set a custom opacity on the bar when you hover over them
                .on("mouseover", (d) => {
                    if (!editing && !editForm) {
                        // d3v3 https://stackoverflow.com/questions/45262172/45262284#comment83459767_45262284
                        // d3v{4/5} https://stackoverflow.com/a/42667743/6201291
                        const thisArrow = d3.event.target
                        d3.select(thisArrow).style("opacity", "0.8")

                        const tooltipElement = (
                            // in IE 11, the Tooltip DS component renders an svg text element
                            // rather than a foreignObject > div container
                            isIE()
                                ? "text"
                                : "div"
                        )
                        const tooltipHeight = (
                            tooltip
                                .select(tooltipElement)
                                .attr("height")
                        )
                        const tooltipWidth = (
                            tooltip
                                .select(tooltipElement)
                                .attr("width")
                        )
                        const tooltipX = (
                            isHorizontal
                                ? (
                                    // rect x attr + rect width attr
                                    x(d.y0) +
                                    (
                                        x(d.y0 + d.y) -
                                        x(d.y0)
                                    ) +
                                    // this offsets the tooltip 11px to the right of the bar to match the mocks
                                    10
                                )
                                : (
                                    // rect x attr
                                    (x(d.x)) +
                                    // rect width attr
                                    x.rangeBand()
                                // center the tooltip
                                ) - (x.rangeBand() / 2) - (tooltipWidth / 2)
                        )
                        const tooltipY = (
                            isHorizontal
                                ? (
                                    (
                                        // rect y attr
                                        y(d.x) +
                                        // rect height attr
                                        y.rangeBand()
                                    ) -
                                    // center the tooltip
                                    (y.rangeBand() / 2) -
                                    (tooltipHeight / 2)
                                )
                                : (
                                    // this places the tooltip at the top of each stacked bar
                                    y(
                                        // rect y attr
                                        (d.y0 || 0) +
                                        (d.y || 0)
                                    ) -
                                    // this offsets the tooltip 11px above the bar to match the mocks
                                    45
                                )
                        )
                        const translateX = (
                            tooltipX <= (width - tooltipWidth)
                                ? tooltipX
                                // if tooltipX is placed outside of the svg content
                                // (if it overflows the svg width),
                                // just make it the width of the svg offset to the width of the tooltip element
                                : (width - tooltipWidth)
                        )
                        const translateY = (
                            tooltipY >= 0
                                ? tooltipY
                                // if tooltipY is placed outside of the svg content (negative value),
                                // just make it 0
                                : 0
                        )

                        showTooltip({
                            tooltip,
                            value: (
                                percentage
                                    // 100% stacked
                                    ? d3.format(".0%")(d.y)
                                    : d.y
                            ),
                            x: translateX,
                            y: translateY,
                        })
                    }
                })
                .on("mouseout", () => {
                    if (!editing && !editForm) {
                        const thisArrow = d3.event.target
                        d3.select(thisArrow).style("opacity", "1")

                        hideTooltip({ tooltip })
                    }
                })
                // though we are using a custom tooltip,
                // we need the browser-native title element as well for accessibility purposes
                .append("title")

                .text((d) => {
                    // 100% stacked
                    if (percentage) {
                        return `${d.key}: ${d3.format(".0%")(d.y)}`
                    }

                    return `${d.key}: ${d.y ? d.y.toLocaleString() : ""}`
                })
        }
    }, [timestamp, colors, height, percentage, width])
